<script setup lang="ts">
import { Toggle } from 'reka-ui';

defineProps<{
  label: string;
  sublabel?: string;
  unstyled?: boolean;
}>();

const modelValue = defineModel<boolean>('selected', { default: false });
</script>

<template>
  <Toggle
    v-model="modelValue"
    class="selector body-small"
    :aria-label="`Select ${label}`"
    :class="{ 'is-unstyled': !!unstyled }"
  >
    <slot>
      <label class="selector-label">{{ label }}</label>
      <span v-if="sublabel" class="selector-sublabel">{{ sublabel }}</span>
    </slot>
  </Toggle>
</template>

<style scoped lang="scss">
.selector {
  --selector-border-color: var(--colour-warm-grey);

  text-align: left;
  // hack due to no lack of vertical trim CSS property
  padding: calc(var(--spacing-ms) - 5px) var(--spacing-ms);
  border-radius: var(--border-radius-m);
  background-color: transparent;
  outline-color: transparent;
  transition: all var(--anim-speed) ease;

  &:not(.is-unstyled) {
    border: 0.5px solid var(--selector-border-color);

    &[data-state="on"] {
      --selector-border-color: var(--colour-black);

      background-color: var(--colour-light-warm-grey);
    }

    &:not(:disabled) {
      &:hover {
        --selector-border-color: var(--colour-black);
      }
    }

    &:focus, &:focus-within, &:focus-visible {
      --selector-border-color: var(--colour-warm-grey);
      outline-color: var(--colour-warm-grey);
      // TODO: make this look like the designs somehow, since CSS has
      // limited border-style options
      // @see https://kovart.github.io/dashed-border-generator/
    }

    &:disabled {
      --selector-border-color: var(--colour-light-warm-grey);
      color: var(--colour-warm-grey);

      &[data-state="on"] {
        --selector-border-color: var(--colour-warm-grey);
        background-color: var(--colour-off-white);
      }
    }
  }

  &:not(:disabled) {
    cursor: pointer;
  }

  &.is-unstyled {
    border: none;
    padding: 0;
  }
}
.selector-label {
  display: block;
  font-weight: 500;
  color: var(--colour-dark-grey);

  &:not(:last-child) {
    margin-bottom: var(--spacing-xxs);
  }

  [data-state="on"] > & {
    color: var(--colour-black);
  }
  :disabled & {
    color: var(--colour-warm-grey);
  }
}
.selector-sublabel {
  line-height: 2rem;
  color: var(--colour-mid-grey);

  :disabled & {
    color: var(--colour-warm-grey);
  }
}
</style>
